import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { Check, ChevronLeft, Clock, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTitle, formatPrice, formatDuration, formatDate, formatTime, imageUrl } from "@/lib/format";
import { DemoBadge, PageLoader } from "@/components/common";
import SlotPicker from "@/components/SlotPicker";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const STEPS = ["Service", "Date & time", "Your details", "Confirm"];

const stepCircleClass = (i, step) => {
  if (i < step) return "bg-teal-600 text-white";
  if (i === step) return "bg-sky-600 text-white";
  return "bg-slate-200 text-slate-500";
};

function Stepper({ step }) {
  return (
    <ol className="flex items-center gap-2" data-testid="booking-stepper">
      {STEPS.map((s, i) => (
        <li key={s} className="flex flex-1 items-center gap-2">
          <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${stepCircleClass(i, step)}`}>
            {i < step ? <Check className="h-4 w-4" /> : i + 1}
          </span>
          <span className={`hidden text-sm font-medium sm:inline ${i === step ? "text-slate-900" : "text-slate-500"}`}>{s}</span>
          {i < STEPS.length - 1 && <span className="h-px flex-1 bg-slate-200" />}
        </li>
      ))}
    </ol>
  );
}

function Summary({ p, service, date, time }) {
  return (
    <aside className="rounded-2xl border border-slate-200 bg-white p-5 lg:sticky lg:top-24" data-testid="booking-summary">
      <div className="flex items-center gap-3">
        <img src={imageUrl(p.profile_image)} alt="" className="h-12 w-12 rounded-xl object-cover" />
        <div className="min-w-0">
          <p className="truncate font-heading font-semibold text-slate-900">{p.business_name}</p>
          <p className="flex items-center gap-1 text-xs text-slate-500"><MapPin className="h-3 w-3" />{p.suburb}, {p.city}</p>
        </div>
      </div>
      {p.is_demo && <DemoBadge className="mt-3" />}
      <dl className="mt-5 space-y-3 border-t border-slate-100 pt-4 text-sm">
        <div className="flex justify-between"><dt className="text-slate-500">Service</dt><dd className="text-right font-medium text-slate-900">{service?.name || "-"}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Duration</dt><dd className="font-medium text-slate-900">{service ? formatDuration(service.duration_minutes) : "-"}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Date</dt><dd className="font-medium text-slate-900">{date ? formatDate(date) : "-"}</dd></div>
        <div className="flex justify-between"><dt className="text-slate-500">Time</dt><dd className="font-medium text-slate-900">{time ? formatTime(time) : "-"}</dd></div>
      </dl>
      <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
        <span className="text-sm text-slate-500">Price (pay provider directly)</span>
        <span className="font-heading text-2xl font-bold text-slate-900" data-testid="summary-price">{service ? formatPrice(service.price) : "-"}</span>
      </div>
    </aside>
  );
}

const BTN = "flex h-12 items-center justify-center rounded-xl px-6 font-semibold transition-all active:scale-[0.98]";

function ServiceStep({ services, serviceId, onSelect }) {
  return (
    <div className="space-y-3" data-testid="step-service">
      <h2 className="font-heading text-xl font-semibold">1. Choose a service</h2>
      {services.map((s) => (
        <button key={s.id} type="button" data-testid={`select-service-${s.id}`} onClick={() => onSelect(s.id)}
          className={`flex w-full items-center justify-between gap-4 rounded-xl border p-4 text-left transition-all ${serviceId === s.id ? "border-sky-600 bg-sky-50 ring-1 ring-sky-600" : "border-slate-200 hover:border-sky-300"}`}>
          <div>
            <p className="font-semibold text-slate-900">{s.name}</p>
            <p className="mt-0.5 flex items-center gap-1 text-xs text-slate-500"><Clock className="h-3 w-3" />{formatDuration(s.duration_minutes)} · {s.service_type}</p>
          </div>
          <span className="font-heading text-lg font-bold">{formatPrice(s.price)}</span>
        </button>
      ))}
    </div>
  );
}

function DetailsStep({ details, setDetails, showAddress }) {
  const setD = (k) => (e) => setDetails({ ...details, [k]: e.target.value });
  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="step-details">
      <h2 className="font-heading text-xl font-semibold sm:col-span-2">3. Your details</h2>
      <div><Label>Full name</Label><Input data-testid="details-name" value={details.customer_name} onChange={setD("customer_name")} className="mt-1.5 h-12" /></div>
      <div><Label>Phone</Label><Input data-testid="details-phone" value={details.customer_phone} onChange={setD("customer_phone")} placeholder="021 123 4567" className="mt-1.5 h-12" /></div>
      <div className="sm:col-span-2"><Label>Email</Label><Input data-testid="details-email" type="email" value={details.customer_email} onChange={setD("customer_email")} className="mt-1.5 h-12" /></div>
      {showAddress && <div className="sm:col-span-2"><Label>Service address</Label><Input data-testid="details-address" value={details.address} onChange={setD("address")} placeholder="Street, suburb, city" className="mt-1.5 h-12" /></div>}
      <div className="sm:col-span-2"><Label>Notes for the provider (optional)</Label><Textarea data-testid="details-notes" value={details.notes} onChange={setD("notes")} rows={3} className="mt-1.5" /></div>
    </div>
  );
}

function ConfirmStep({ service, date, time, details }) {
  const rows = [["Service", service.name], ["Date", formatDate(date)], ["Time", formatTime(time)], ["Name", details.customer_name], ["Email", details.customer_email], ["Phone", details.customer_phone]];
  if (details.address) rows.push(["Address", details.address]);
  return (
    <div data-testid="step-confirm">
      <h2 className="font-heading text-xl font-semibold">4. Review & confirm</h2>
      <dl className="mt-5 grid gap-4 text-sm sm:grid-cols-2">
        {rows.map(([k, v]) => <div key={k} className="rounded-xl bg-slate-50 p-4"><dt className="text-xs uppercase tracking-wider text-slate-500">{k}</dt><dd className="mt-1 font-semibold text-slate-900">{v}</dd></div>)}
      </dl>
      <p className="mt-5 text-xs text-slate-500">No payment is taken online. Your booking request is sent to the provider, who will confirm it.</p>
    </div>
  );
}

function StepNav({ step, canNext, busy, onBack, onNext, onConfirm }) {
  return (
    <div className="mt-8 flex gap-3 border-t border-slate-100 pt-6">
      {step > 0 && <button data-testid="booking-back-button" onClick={onBack} className={`${BTN} border border-slate-300 text-slate-700`}>Back</button>}
      {step < 3 && <button data-testid="booking-next-button" disabled={!canNext} onClick={onNext} className={`${BTN} flex-1 bg-sky-600 text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:opacity-40`}>Continue</button>}
      {step === 3 && (
        <button data-testid="confirm-booking-button" disabled={busy} onClick={onConfirm} className={`${BTN} flex-1 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-60`}>
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Confirm booking"}
        </button>
      )}
    </div>
  );
}

const isDetailsValid = (d) => d.customer_name.trim().length > 1 && /\S+@\S+\.\S+/.test(d.customer_email) && d.customer_phone.trim().length >= 5;

export default function BookingFlow() {
  useTitle("Book an appointment");
  const { providerId } = useParams();
  const [search] = useSearchParams();
  const initialService = search.get("service");
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [step, setStep] = useState(0);
  const [serviceId, setServiceId] = useState(initialService);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [details, setDetails] = useState({ customer_name: user?.name || "", customer_email: user?.email || "", customer_phone: user?.phone || "", address: "", notes: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/providers/${providerId}`).then(({ data }) => {
      setP(data);
      if (initialService && data.services.some((s) => s.id === initialService)) setStep(1);
    }).catch(() => navigate("/services"));
  }, [providerId, initialService, navigate]);

  if (!p) return <PageLoader />;
  const service = p.services.find((s) => s.id === serviceId);
  const selectService = (id) => { setServiceId(id); setDate(null); setTime(null); };

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/bookings", { provider_id: p.id, service_id: serviceId, date, start_time: time, ...details });
      navigate(`/bookings/${data.id}/confirmed`);
    } catch (e) {
      toast.error(formatApiError(e));
      if (e.response?.status === 409) { setTime(null); setStep(1); }
    } finally { setBusy(false); }
  };

  const canNext = [!!service, !!(date && time), isDetailsValid(details)][step];

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="booking-flow-page">
      <Link to={`/provider/${p.id}`} className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-sky-700" data-testid="back-to-provider"><ChevronLeft className="h-4 w-4" />Back to profile</Link>
      <h1 className="mt-3 font-heading text-3xl font-bold tracking-tight text-slate-900">Book with {p.business_name}</h1>
      <div className="mt-6"><Stepper step={step} /></div>
      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_340px]">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-7">
          {step === 0 && <ServiceStep services={p.services} serviceId={serviceId} onSelect={selectService} />}
          {step === 1 && (
            <div data-testid="step-datetime">
              <h2 className="mb-5 font-heading text-xl font-semibold">2. Pick a date & time</h2>
              <SlotPicker providerId={p.id} serviceId={serviceId} date={date} time={time} onDate={setDate} onTime={setTime} />
            </div>
          )}
          {step === 2 && <DetailsStep details={details} setDetails={setDetails} showAddress={service?.service_type === "At your place"} />}
          {step === 3 && <ConfirmStep service={service} date={date} time={time} details={details} />}
          <StepNav step={step} canNext={canNext} busy={busy} onBack={() => setStep(step - 1)} onNext={() => setStep(step + 1)} onConfirm={confirm} />
        </div>
        <Summary p={p} service={service} date={date} time={time} />
      </div>
    </div>
  );
}
